'use client';

import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { useRef } from "react";

import { DEFAULT_GALLERY_IMAGES } from "./GalleryPopup";
import RoomCard from "@/components/pages/hotel/detail/RoomCard";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import RoomSearchForm from "@/components/pages/hotel/detail/roomSearchForm/RoomSearchForm";

// API'den gelen RoomTypes'ı RoomCard formatına dönüştür (t: çeviri fonksiyonu)
const transformRoomTypesToRooms = (roomTypes = [], t) => {
  return roomTypes.map((roomType) => {
    // RoomType'dan images oluştur
    const images = (roomType.Photos || [roomType.Image].filter(Boolean)).map((url, index) => ({
      id: `room-${roomType.RoomId}-img-${index}`,
      src: url,
      alt: roomType.RoomName || `Room image ${index + 1}`,
    }));

    // Features oluştur
    const features = [];
    if (roomType.BedOptionText) {
      features.push({
        icon: byPrefixAndName.fak['bed-front'],
        label: roomType.BedOptionText,
      });
    }
    if (roomType.RoomSize) {
      features.push({
        icon: byPrefixAndName.fak['metrekare'],
        label: `${roomType.RoomSize} ${roomType.SizeUnit || 'm2'}`,
      });
    }
    if (roomType.MaxPerson) {
      features.push({
        icon: byPrefixAndName.fak['user-v2'],
        label: `${roomType.MaxPerson} ${t ? t('PersonCapacity') : 'Kişi Kapasitesi'}`,
      });
    }

    // RoomPrices'ı options formatına dönüştür
    const options = (roomType.RoomPrices || []).map((price, index) => {
      const roomPricing = price.RoomPricing || {};
      const discountRate = roomPricing.DiscountRate || 0;
      const grossPrice = roomPricing.GrossPrice || 0;
      const netPrice = roomPricing.NetPrice || 0;

      // Eğer DiscountRate > 0 ise indirim var
      // GrossPrice eski fiyat, NetPrice yeni fiyat
      // Eğer DiscountRate === 0 ise indirim yok, sadece GrossPrice göster
      const hasDiscount = discountRate > 0;

      return {
        id: price.PriceKey || `option-${roomType.RoomId}-${index}`,
        roomId: roomType.RoomId,
        label: price.PensionLabel || 'BB',
        refundable: price.CancellationPolicy?.IsRefundable || false,
        // Her zaman GrossPrice'ı original price olarak kullan
        price: grossPrice,
        // İndirim varsa NetPrice, yoksa GrossPrice (indirimsiz durumda aynı fiyat)
        discountedPrice: hasDiscount ? netPrice : grossPrice,
        // İndirim metni (DiscountRate > 0 ise göster)
        discountText: hasDiscount
          ? t("discountBadge", { discount: discountRate })
          : undefined,
        discountRate: discountRate,
        cancellationPolicy: price.CancellationPolicy,
      };
    });

    return {
      id: roomType.RoomId,
      images: images.length > 0 ? images : DEFAULT_GALLERY_IMAGES.slice(0, 3),
      label: roomType.RoomName || 'Oda',
      features,
      options: options.length > 0 ? options : [{
        id: `default-${roomType.RoomId}`,
        roomId: roomType.RoomId,
        label: 'Standart',
        refundable: false,
        price: 0,
        discountedPrice: 0,
      }],
      description: roomType.Description,
      amenityGroups: roomType.AmenityGroups,
    };
  });
};

const HotelRooms = ({ hotelSlug, checkinDate, checkoutDate, guests, roomsData = [], providerId, reference, normalSearchReference, onAddToCart }) => {
  const t = useTranslationDetector('hotel');
  const roomSearchFormRef = useRef(null);

  // API'den gelen roomsData'yı dönüştür
  const rooms = roomsData.length > 0
    ? transformRoomTypesToRooms(roomsData, t)
    : [];

  // Eğer oda yoksa komponenti render etme
  if (rooms.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4 sm:gap-6">
      <div className="text-xl sm:text-[23px] md:text-[29px]! font-semibold">
        {t('rooms')}
      </div>
      <div>
        <RoomSearchForm
          ref={roomSearchFormRef}
          initialLocationSlug={hotelSlug}
          initialCheckinDate={checkinDate}
          initialCheckoutDate={checkoutDate}
          initialGuests={guests}
        />
      </div>
      <div className="flex flex-col gap-4 sm:gap-6">
        {rooms.map((room) => (
          <RoomCard
            {...room}
            key={room.id}
            guests={guests}
            checkinDate={checkinDate}
            checkoutDate={checkoutDate}
            providerId={providerId}
            reference={reference}
            normalSearchReference={normalSearchReference}
            onAddToCart={onAddToCart}
            onShowPriceClick={() => {
              // Datepicker'ı aç
              roomSearchFormRef.current?.openDatePicker();
            }}
          />
        ))}
      </div>
    </div>
  )
}

export default HotelRooms;